import * as i0 from '@angular/core';
import { Injectable, inject, ChangeDetectorRef, Input, ViewChild, ViewEncapsulation, ChangeDetectionStrategy, Component, DestroyRef, booleanAttribute, ContentChildren, NgModule } from '@angular/core';
import * as i1 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { ReplaySubject } from 'rxjs';
import { Directionality } from '@angular/cdk/bidi';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i2 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["template"];
const _c1 = ["*"];
function NzTimelineItemComponent_ng_template_0_Conditional_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzLabel);
  }
}
function NzTimelineItemComponent_ng_template_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵtemplate(1, NzTimelineItemComponent_ng_template_0_Conditional_1_ng_container_1_Template, 2, 1, "ng-container", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzLabel);
  }
}
function NzTimelineItemComponent_ng_template_0_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzDot);
  }
}
function NzTimelineItemComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 1);
    i0.ɵɵconditionalCreate(1, NzTimelineItemComponent_ng_template_0_Conditional_1_Template, 2, 1, "div", 2);
    i0.ɵɵelement(2, "div", 3);
    i0.ɵɵelementStart(3, "div", 4);
    i0.ɵɵtemplate(4, NzTimelineItemComponent_ng_template_0_ng_container_4_Template, 2, 1, "ng-container", 5);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div", 6);
    i0.ɵɵprojection(6);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("ant-timeline-item-right", (ctx_r0.nzPosition || ctx_r0.position) === "right")("ant-timeline-item-left", (ctx_r0.nzPosition || ctx_r0.position) === "left")("ant-timeline-item-last", ctx_r0.isLast);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzLabel ? 1 : -1);
    i0.ɵɵadvance(2);
    i0.ɵɵstyleProp("border-color", ctx_r0.borderColor);
    i0.ɵɵclassProp("ant-timeline-item-head-red", ctx_r0.nzColor === "red")("ant-timeline-item-head-blue", ctx_r0.nzColor === "blue")("ant-timeline-item-head-green", ctx_r0.nzColor === "green")("ant-timeline-item-head-gray", ctx_r0.nzColor === "gray")("ant-timeline-item-head-custom", !!ctx_r0.nzDot);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzDot);
  }
}
function NzTimelineComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0, 2);
  }
  if (rf & 2) {
    i0.ɵɵnextContext();
    const pendingTemplate_r1 = i0.ɵɵreference(6);
    i0.ɵɵproperty("ngTemplateOutlet", pendingTemplate_r1);
  }
}
function NzTimelineComponent_For_3_ng_template_0_Template(rf, ctx) {}
function NzTimelineComponent_For_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzTimelineComponent_For_3_ng_template_0_Template, 0, 0, "ng-template", 2);
  }
  if (rf & 2) {
    const item_r2 = ctx.$implicit;
    i0.ɵɵproperty("ngTemplateOutlet", item_r2.template);
  }
}
function NzTimelineComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0, 2);
  }
  if (rf & 2) {
    i0.ɵɵnextContext();
    const pendingTemplate_r1 = i0.ɵɵreference(6);
    i0.ɵɵproperty("ngTemplateOutlet", pendingTemplate_r1);
  }
}
function NzTimelineComponent_ng_template_5_Conditional_0_ng_container_3_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 8);
  }
}
function NzTimelineComponent_ng_template_5_Conditional_0_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵconditionalCreate(2, NzTimelineComponent_ng_template_5_Conditional_0_ng_container_3_Conditional_2_Template, 1, 0, "nz-icon", 8);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.nzPendingDot, " ");
    i0.ɵɵadvance();
    i0.ɵɵconditional(!ctx_r2.nzPendingDot ? 2 : -1);
  }
}
function NzTimelineComponent_ng_template_5_Conditional_0_ng_container_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.isPendingBoolean ? "" : ctx_r2.nzPending, " ");
  }
}
function NzTimelineComponent_ng_template_5_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 3);
    i0.ɵɵelement(1, "div", 4);
    i0.ɵɵelementStart(2, "div", 5);
    i0.ɵɵtemplate(3, NzTimelineComponent_ng_template_5_Conditional_0_ng_container_3_Template, 3, 2, "ng-container", 6);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div", 7);
    i0.ɵɵtemplate(5, NzTimelineComponent_ng_template_5_Conditional_0_ng_container_5_Template, 2, 1, "ng-container", 6);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r2.nzPendingDot);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r2.nzPending);
  }
}
function NzTimelineComponent_ng_template_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, NzTimelineComponent_ng_template_5_Conditional_0_Template, 6, 2, "li", 3);
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ctx_r2.nzPending ? 0 : -1);
  }
}
let TimelineService = /*#__PURE__*/(() => {
  class TimelineService {
    check$ = new ReplaySubject(1);
    markForCheck() {
      this.check$.next();
    }
    static ɵfac = function TimelineService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || TimelineService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: TimelineService,
      factory: TimelineService.ɵfac
    });
  }
  return TimelineService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const TimelineTimeDefaultColors = ['red', 'blue', 'green', 'grey', 'gray'];

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
function isDefaultColor(color) {
  return TimelineTimeDefaultColors.findIndex(i => i === color) !== -1;
}
let NzTimelineItemComponent = /*#__PURE__*/(() => {
  class NzTimelineItemComponent {
    cdr = inject(ChangeDetectorRef);
    timelineService = inject(TimelineService);
    template;
    nzPosition;
    nzColor = 'blue';
    nzDot;
    nzLabel;
    isLast = false;
    borderColor = null;
    position;
    ngOnChanges(changes) {
      this.timelineService.markForCheck();
      const {
        nzColor
      } = changes;
      if (nzColor) {
        this.updateCustomColor();
      }
    }
    detectChanges() {
      this.cdr.detectChanges();
    }
    updateCustomColor() {
      this.borderColor = isDefaultColor(this.nzColor) ? null : this.nzColor;
    }
    static ɵfac = function NzTimelineItemComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzTimelineItemComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzTimelineItemComponent,
      selectors: [["nz-timeline-item"], ["", "nz-timeline-item", ""]],
      viewQuery: function NzTimelineItemComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.template = _t.first);
        }
      },
      inputs: {
        nzPosition: "nzPosition",
        nzColor: "nzColor",
        nzDot: "nzDot",
        nzLabel: "nzLabel"
      },
      exportAs: ["nzTimelineItem"],
      features: [i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c1,
      decls: 2,
      vars: 0,
      consts: [["template", ""], [1, "ant-timeline-item"], [1, "ant-timeline-item-label"], [1, "ant-timeline-item-tail"], [1, "ant-timeline-item-head"], [4, "nzStringTemplateOutlet"], [1, "ant-timeline-item-content"]],
      template: function NzTimelineItemComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, NzTimelineItemComponent_ng_template_0_Template, 7, 20, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        }
      },
      dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzTimelineItemComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzTimelineComponent = /*#__PURE__*/(() => {
  class NzTimelineComponent {
    cdr = inject(ChangeDetectorRef);
    timelineService = inject(TimelineService);
    directionality = inject(Directionality);
    destroyRef = inject(DestroyRef);
    listOfItems;
    nzMode = 'left';
    nzPending;
    nzPendingDot;
    nzReverse = false;
    isPendingBoolean = false;
    timelineItems = [];
    dir = 'ltr';
    hasLabelItem = false;
    ngOnChanges(changes) {
      const {
        nzMode,
        nzReverse,
        nzPending
      } = changes;
      if (simpleChangeActivated(nzMode) || simpleChangeActivated(nzReverse)) {
        this.updateChildren();
      }
      if (nzPending) {
        this.isPendingBoolean = nzPending.currentValue === true;
      }
    }
    ngOnInit() {
      this.timelineService.check$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.cdr.markForCheck();
      });
      this.directionality.change?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.directionality.value;
    }
    ngAfterContentInit() {
      this.updateChildren();
      this.listOfItems.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.updateChildren();
      });
    }
    updateChildren() {
      if (this.listOfItems && this.listOfItems.length) {
        const length = this.listOfItems.length;
        let hasLabelItem = false;
        this.listOfItems.forEach((item, index) => {
          item.isLast = !this.nzReverse ? index === length - 1 : index === 0;
          item.position = getInferredTimelineItemPosition(index, this.nzMode);
          if (!hasLabelItem && item.nzLabel) {
            hasLabelItem = true;
          }
          item.detectChanges();
        });
        this.timelineItems = this.nzReverse ? this.listOfItems.toArray().reverse() : this.listOfItems.toArray();
        this.hasLabelItem = hasLabelItem;
      } else {
        this.timelineItems = [];
        this.hasLabelItem = false;
      }
      this.cdr.markForCheck();
    }
    static ɵfac = function NzTimelineComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzTimelineComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzTimelineComponent,
      selectors: [["nz-timeline"]],
      contentQueries: function NzTimelineComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NzTimelineItemComponent, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.listOfItems = _t);
        }
      },
      inputs: {
        nzMode: "nzMode",
        nzPending: "nzPending",
        nzPendingDot: "nzPendingDot",
        nzReverse: [2, "nzReverse", "nzReverse", booleanAttribute]
      },
      exportAs: ["nzTimeline"],
      features: [i0.ɵɵProvidersFeature([TimelineService]), i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c1,
      decls: 8,
      vars: 14,
      consts: [["pendingTemplate", ""], [1, "ant-timeline"], [3, "ngTemplateOutlet"], [1, "ant-timeline-item", "ant-timeline-item-pending"], [1, "ant-timeline-item-tail"], [1, "ant-timeline-item-head", "ant-timeline-item-head-custom", "ant-timeline-item-head-blue"], [4, "nzStringTemplateOutlet"], [1, "ant-timeline-item-content"], ["nzType", "loading"]],
      template: function NzTimelineComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "ul", 1);
          i0.ɵɵconditionalCreate(1, NzTimelineComponent_Conditional_1_Template, 1, 1, "ng-container", 2);
          i0.ɵɵrepeaterCreate(2, NzTimelineComponent_For_3_Template, 1, 1, null, 2, i0.ɵɵrepeaterTrackByIdentity);
          i0.ɵɵconditionalCreate(4, NzTimelineComponent_Conditional_4_Template, 1, 1, "ng-container", 2);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(5, NzTimelineComponent_ng_template_5_Template, 1, 1, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
          i0.ɵɵprojection(7);
        }
        if (rf & 2) {
          i0.ɵɵclassProp("ant-timeline-label", ctx.hasLabelItem)("ant-timeline-right", !ctx.hasLabelItem && ctx.nzMode === "right")("ant-timeline-alternate", ctx.nzMode === "alternate" || ctx.nzMode === "custom")("ant-timeline-pending", !!ctx.nzPending)("ant-timeline-reverse", ctx.nzReverse)("ant-timeline-rtl", ctx.dir === "rtl");
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx.nzReverse ? 1 : -1);
          i0.ɵɵadvance();
          i0.ɵɵrepeater(ctx.timelineItems);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(!ctx.nzReverse ? 4 : -1);
        }
      },
      dependencies: [NgTemplateOutlet, NzOutletModule, i1.NzStringTemplateOutletDirective, NzIconModule, i2.NzIconDirective],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzTimelineComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function simpleChangeActivated(simpleChange) {
  return !!(simpleChange && (simpleChange.previousValue !== simpleChange.currentValue || simpleChange.isFirstChange()));
}
function getInferredTimelineItemPosition(index, mode) {
  if (mode === 'custom') {
    return undefined;
  } else if (mode === 'left' || mode === 'right') {
    return mode;
  } else {
    return mode === 'alternate' && index % 2 === 0 ? 'left' : 'right';
  }
}

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzTimelineModule = /*#__PURE__*/(() => {
  class NzTimelineModule {
    static ɵfac = function NzTimelineModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzTimelineModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NzTimelineModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [NzTimelineItemComponent, NzTimelineComponent]
    });
  }
  return NzTimelineModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzTimelineComponent, NzTimelineItemComponent, NzTimelineModule, TimelineService };
